import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Fade,
  Button,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  SportsEsports,
  Palette,
  RocketLaunch,
  Stadium,
  EmojiEvents,
  AutoAwesome,
  Brush,
  Send,
  CampaignOutlined,
  WorkspacePremium,
} from "@mui/icons-material";

const BingoStepsCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [animating, setAnimating] = useState(false);

  const steps = [
    {
      id: 1,
      emoji: "🎮",
      title: "Choose a Theme",
      description:
        "From wellness to company values or create your own",
      color: "#FF6B6B",
      bgColor: "rgba(255, 107, 107, 0.1)",
      icon: SportsEsports,
      decorativeIcons: [AutoAwesome, PlayArrow],
    },
    {
      id: 2,
      emoji: "🎨",
      title: "Customize the Cards",
      description:
        " Brand it with your colors, slogans, or culture",
      color: "#4ECDC4",
      bgColor: "rgba(78, 205, 196, 0.1)",
      icon: Palette,
      decorativeIcons: [Brush, AutoAwesome],
    },
    {
      id: 3,
      emoji: "🚀",
      title: "Distribute Digitally or Physically",
      description:
        "Email cards or hand them out — your choice",
      color: "#45B7D1",
      bgColor: "rgba(69, 183, 209, 0.1)",
      icon: RocketLaunch,
      decorativeIcons: [Send, AutoAwesome],
    },
    {
      id: 4,
      emoji: "🎯",
      title: " Host the Game",
      description:
        "Host epic battles in-person, on Discord, Zoom, or create hybrid tournaments that bring everyone together",
      color: "#96CEB4",
      bgColor: "rgba(150, 206, 180, 0.1)",
      icon: Stadium,
      decorativeIcons: [CampaignOutlined, AutoAwesome],
    },
    {
      id: 5,
      emoji: "🏆",
      title: "Crown Your Champions",
      description:
        "Award epic loot, capture victory moments, and create legendary memories that will echo through the ages",
      color: "#F7B731",
      bgColor: "rgba(247, 183, 49, 0.1)",
      icon: EmojiEvents,
      decorativeIcons: [WorkspacePremium, AutoAwesome],
    },
  ];

  // Auto-advance functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, steps.length]);

  const goToStep = (index) => {
    if (animating || index === currentStep) return;
    setAnimating(true);
    setCurrentStep(index);
    setIsAutoPlaying(false);
    setTimeout(() => setAnimating(false), 300);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  const currentStepData = steps[currentStep];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes gradientMove {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes pulse {
          0%, 100% { 
            opacity: 0.3; 
            transform: scale(1);
          }
          50% { 
            opacity: 0.6; 
            transform: scale(1.1);
          }
        }
        
        @keyframes bounce {
          0%, 100% { 
            transform: translateY(0);
          }
          50% { 
            transform: translateY(-10px);
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes dotPulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 0.8;
          }
          50% { 
            transform: scale(1.2);
            opacity: 1;
          }
        }

        @keyframes glow {
          0%, 100% { 
            filter: drop-shadow(0 0 10px currentColor);
          }
          50% { 
            filter: drop-shadow(0 0 20px currentColor);
          }
        }

        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg);
          }
          33% { 
            transform: translateY(-10px) rotate(5deg);
          }
          66% { 
            transform: translateY(5px) rotate(-5deg);
          }
        }

        .slide-in {
          animation: slideIn 0.5s ease-out;
        }

        .dot-active {
          animation: dotPulse 1.5s ease-in-out infinite;
        }

        .gaming-glow {
          animation: glow 2s ease-in-out infinite;
        }

        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>

      {/* Enhanced Gaming Background Elements */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        {[...Array(12)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              left: `${5 + ((i * 15) % 90)}%`,
              top: `${10 + ((i * 11) % 80)}%`,
              width: `${15 + (i % 4) * 8}px`,
              height: `${15 + (i % 4) * 8}px`,
              backgroundColor: [
                "#fbbf24",
                "#ec4899",
                "#8b5cf6",
                "#06d6a0",
                "#f72585",
              ][i % 5],
              borderRadius: i % 3 === 0 ? "50%" : "20%",
              opacity: 0.4,
              animation: `pulse ${
                2 + (i % 3) * 0.5
              }s cubic-bezier(0.4, 0, 0.2, 1) infinite ${i * 0.3}s`,
              transform: i % 2 === 0 ? "rotate(45deg)" : "rotate(-45deg)",
            }}
          />
        ))}
      </Box>

      {/* Main Container */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          maxWidth: "80rem",
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", marginBottom: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 900,
              lineHeight: 1.2,
              mb: 2,
              fontSize: { xs: "2rem", lg: "3rem" },
              textShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
            }}
          >
            🎮 How Bingo Social Works 🎮
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontWeight: 400,
              fontSize: { xs: "1.1rem", md: "1.3rem" },
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
            }}
          >
            Level up your team experience with these epic gaming strategies
          </Typography>
        </Box>

        {/* Main Card */}
        <Box sx={{ maxWidth: "80rem", mx: "auto" }}>
          <Fade in={!animating} timeout={500}>
            <Paper
              elevation={20}
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                padding: { xs: 4, md: 6 },
                border: "1px solid rgba(255, 255, 255, 0.3)",
                transform: animating ? "scale(0.95)" : "scale(1)",
                opacity: animating ? 0.5 : 1,
                transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(45deg, ${currentStepData.color}15, transparent)`,
                  borderRadius: "24px",
                  pointerEvents: "none",
                },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: { xs: 4, md: 6 },
                  alignItems: "center",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* Content Side */}
                <Box
                  sx={{
                    textAlign: { xs: "center", md: "left" },
                    animation: !animating ? "slideIn 0.5s ease-out" : "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", md: "flex-start" },
                      marginBottom: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "3rem", md: "4rem" },
                        marginRight: 2,
                        filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))",
                        animation: "bounce 2s infinite",
                      }}
                    >
                      {currentStepData.emoji}
                    </Typography>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        background: `linear-gradient(135deg, ${currentStepData.color}, #f97316)`,
                        color: "white",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        boxShadow: `0 8px 20px ${currentStepData.color}40`,
                        border: "2px solid rgba(255, 255, 255, 0.3)",
                      }}
                    >
                      {currentStepData.id}
                    </Avatar>
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: "1.8rem", md: "2.5rem" },
                      fontWeight: "bold",
                      color: "#1a1a1a",
                      marginBottom: 2,
                      lineHeight: 1.2,
                    }}
                  >
                    {currentStepData.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#4a5568",
                      fontSize: { xs: "1.1rem", md: "1.25rem" },
                      lineHeight: 1.6,
                      fontWeight: 400,
                    }}
                  >
                    {currentStepData.description}
                  </Typography>
                </Box>

                {/* Visual Side */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {/* Decorative floating icons */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "10%",
                      left: "10%",
                      color: currentStepData.color,
                      opacity: 0.3,
                      animation: "float 4s ease-in-out infinite",
                      animationDelay: "0.5s",
                    }}
                  >
                    {React.createElement(currentStepData.decorativeIcons[0], {
                      sx: { fontSize: "2rem" },
                    })}
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      top: "20%",
                      right: "15%",
                      color: currentStepData.color,
                      opacity: 0.3,
                      animation: "float 4s ease-in-out infinite",
                      animationDelay: "1.5s",
                    }}
                  >
                    {React.createElement(currentStepData.decorativeIcons[1], {
                      sx: { fontSize: "1.5rem" },
                    })}
                  </Box>

                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(135deg, ${currentStepData.color}30, rgba(236, 72, 153, 0.1))`,
                        borderRadius: "50%",
                        filter: "blur(40px)",
                        animation: "pulse 3s ease-in-out infinite",
                      }}
                    />
                    <Paper
                      elevation={15}
                      sx={{
                        position: "relative",
                        background: `linear-gradient(135deg, ${currentStepData.color}10, rgba(255, 255, 255, 0.8))`,
                        backdropFilter: "blur(20px)",
                        borderRadius: "50%",
                        padding: 6,
                        border: `3px solid ${currentStepData.color}40`,
                        transition:
                          "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        width: "200px",
                        height: "200px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <Box
                        className="gaming-glow"
                        sx={{
                          color: currentStepData.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {React.createElement(currentStepData.icon, {
                          sx: { fontSize: "5rem" },
                        })}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Box>

        {/* Navigation dots */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            marginTop: 4,
            flexWrap: "wrap",
          }}
        >
          {steps.map((_, index) => (
            <Box
              key={index}
              onClick={() => goToStep(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor:
                  index === currentStep
                    ? currentStepData.color
                    : "rgba(255, 255, 255, 0.4)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                transform: index === currentStep ? "scale(1.3)" : "scale(1)",
                boxShadow:
                  index === currentStep
                    ? `0 4px 12px ${currentStepData.color}60`
                    : "none",
                "&:hover": {
                  transform: "scale(1.2)",
                  backgroundColor:
                    index === currentStep
                      ? currentStepData.color
                      : "rgba(255, 255, 255, 0.7)",
                },
              }}
            />
          ))}
        </Box>

        {/* Enhanced Progress Indicators */}
        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 4,
            marginTop: 3,
            flexWrap: "wrap",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              borderRadius: "2rem",
              padding: "12px 24px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontWeight: 700,
                fontSize: "1.1rem",
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
              }}
            >
              <Box
                component="span"
                sx={{ 
                  color: currentStepData.color, 
                  fontSize: "1.3rem",
                  filter: "drop-shadow(0 0 8px currentColor)"
                }}
              >
                {currentStep + 1}
              </Box>
              <Box component="span" sx={{ mx: 1, opacity: 0.7 }}>
                /
              </Box>
              <Box component="span" sx={{ opacity: 0.8 }}>
                {steps.length}
              </Box>
            </Typography>
          </Paper>

          
        </Box> */}
      </Box>
    </Box>
  );
};

export default BingoStepsCarousel;
